import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableCell as MuiTableCell,
    Button
} from '@mui/material';
import { styled, tableCellClasses } from '@mui/material';
import { PrimaryButton } from '../components';
import InfoHeader from '../components/InfoHeader';
import BasicMenu from '../components/BasicMenu';
import { metaGlobalService } from '../services/MetaGlobalService';
import MetaGlobal from '../types/negocio/MetaGlobal';
import toastService from '../utils/toastService';
import MetaGlobalModal from '../components/modais/MetaGlobalModal';
import LoadingIndicator from '../components/LoadingIndicator';

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#042E4B',
        color: theme.palette.common.white,
        fontWeight: 'bold'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const MetaGlobalPage: React.FC = () => {
    const [metas, setMetas] = useState<MetaGlobal[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalAberto, setModalAberto] = useState(false);
    const [modoEdicao, setModoEdicao] = useState(false);
    const [metaGlobalSelecionada, setMetaGlobalSelecionada] = useState<MetaGlobal>({} as MetaGlobal);

    const criarMetaGlobalVazia = (): MetaGlobal => ({
        area: '',
        meta: '',
        ordem: metas.length + 1
    });

    useEffect(() => {
        fetchMetas();
    }, []);

    const fetchMetas = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await metaGlobalService.listarTodos();
            
            if (Array.isArray(data)) {
                setMetas(data);
            } else {
                console.error('Resposta inesperada ao carregar metas globais:', data);
                toastService.error('Formato de dados inesperado. Entre em contato com o suporte.');
                setMetas([]);
            }
        } catch (error) {
            console.error('Erro ao carregar metas globais:', error);
            setError("Não foi possível carregar os dados das metas globais. Por favor, tente novamente.");
            toastService.error('Erro ao carregar metas globais. Tente novamente mais tarde.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (index: number) => {
        setMetaGlobalSelecionada(metas[index]);
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleNovaMetaGlobal = () => {
        setMetaGlobalSelecionada(criarMetaGlobalVazia());
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleViewMore = (index: number) => {
        setMetaGlobalSelecionada(metas[index]);
        setModoEdicao(false);
        setModalAberto(true);
    };

    const handleSaveMetaGlobal = async (data: MetaGlobal) => {
        try {
            const isNovaMetaGlobal = !data.guid || data.guid.trim() === '';
            
            if (isNovaMetaGlobal) {
                data.ordem = metas.length + 1; // Definir ordem para nova meta
                await metaGlobalService.save(data);
                toastService.success('Meta global criada com sucesso!');
            } else {
                await metaGlobalService.save(data);
                toastService.success('Meta global atualizada com sucesso!');
            }
            
            setModalAberto(false);
            
            // Atualizar a lista
            const listaAtualizada = await metaGlobalService.listarTodos();
            setMetas(listaAtualizada);
        } catch (error) {
            console.error('Erro ao salvar meta global:', error);
            toastService.error('Erro ao salvar meta global.');
        }
    };

    const handleDelete = async (index: number) => {
        const metaToDelete = metas[index];

        if (!metaToDelete?.guid) {
            toastService.error("Meta global não encontrada.");
            return;
        }

        try {
            await metaGlobalService.deletar(metaToDelete.guid);
            
            // Atualizar ordem das metas restantes
            const metasRestantes = metas.filter((_, i) => i !== index);
            const metasComNovaOrdem = metasRestantes.map((meta, i) => ({
                ...meta,
                ordem: i + 1
            }));
            
            // Salvar nova ordem no servidor
            if (metasComNovaOrdem.length > 0) {
                await metaGlobalService.salvarTodos(metasComNovaOrdem);
            }
            
            setMetas(metasComNovaOrdem);
            toastService.success("Meta global excluída com sucesso!");
        } catch (error) {
            console.error("Erro ao excluir meta global:", error);
            toastService.error("Erro ao excluir meta global. Tente novamente.");
        }
    };

    // Show loading state when data is being fetched
    if (isLoading) {
        return <LoadingIndicator fullHeight message="Carregando metas globais..." />;
    }

    // Show error state only after loading is complete and there's an error
    if (error && !isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <Typography color="error">{error}</Typography>
                <PrimaryButton onClick={() => window.location.reload()}>
                    Tentar Novamente
                </PrimaryButton>
            </Box>
        );
    }

    return (
        <Box>
            <InfoHeader
                title="Metas Globais"
                message="Adicione e faça alterações nas metas globais da sua empresa."
                isImportant={true}
            />

            <Paper elevation={3} sx={{ mt: 2 }}>
                <TableContainer sx={{ borderRadius: '8px' }}>
                    <Table aria-label="metas globais table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell><strong>Área</strong></StyledTableCell>
                                <StyledTableCell><strong>Meta</strong></StyledTableCell>
                                <StyledTableCell align="center"><strong>Ações</strong></StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {metas.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={3} align="center">
                                        <Typography sx={{ color: 'text.secondary', py: 2 }}>
                                            Nenhuma meta global cadastrada
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                metas.map((meta, index) => (
                                    <StyledTableRow key={index}>
                                        <StyledTableCell 
                                            component="th" 
                                            scope="row"
                                            onClick={() => handleViewMore(index)}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <Typography variant="body2" sx={{ fontSize: 16, "&:hover": { textDecoration: "underline", color: "primary.main" } }}>
                                                {meta.area}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell component="th" scope="row">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: 16,
                                                    color: meta.meta && meta.meta.trim() !== ''
                                                        ? 'text.primary'
                                                        : 'secondary.main'
                                                }}
                                            >
                                                {meta.meta || 'Sem meta'}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <BasicMenu
                                                onEdit={() => handleEdit(index)}
                                                onDelete={() => handleDelete(index)}
                                                onViewMore={() => handleViewMore(index)}
                                            />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <PrimaryButton variant="contained" onClick={handleNovaMetaGlobal}>
                    Adicionar
                </PrimaryButton>
            </Box>

            <MetaGlobalModal
                open={modalAberto}
                onClose={() => setModalAberto(false)}
                metaGlobal={metaGlobalSelecionada}
                modoEdicao={modoEdicao}
                setMetaGlobal={modoEdicao ? setMetaGlobalSelecionada : undefined}
                onSave={modoEdicao ? handleSaveMetaGlobal : undefined}
                onEditClick={() => setModoEdicao(true)}
                isNovaMetaGlobal={!metaGlobalSelecionada?.guid || metaGlobalSelecionada.guid.trim() === ''}
            />
        </Box>
    );
};

export default MetaGlobalPage;